import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

const LETTERS = "About".split("");

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const ringL = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const ringR = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const discScale = useTransform(scrollYProgress, [0, 1], [1, 0.82]);
  const discY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const drift = (dx, dy, d) =>
    reduce
      ? {}
      : {
          animate: { x: [0, dx, 0], y: [0, dy, 0] },
          transition: { duration: d, repeat: Infinity, ease: "easeInOut" },
        };

  const scrollDown = () =>
    document.getElementById("about")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });

  return (
    <header ref={ref} className="au-hero">
      <div className="au-hero__grid" aria-hidden="true" />
      <motion.span className="au-hero__orb au-hero__orb--a" aria-hidden="true" {...drift(60, -40, 16)} />
      <motion.span className="au-hero__orb au-hero__orb--b" aria-hidden="true" {...drift(-50, 36, 19)} />

      <motion.span
        className="au-hero__ring au-hero__ring--l"
        aria-hidden="true"
        style={reduce ? undefined : { x: ringL }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.span
        className="au-hero__ring au-hero__ring--r"
        aria-hidden="true"
        style={reduce ? undefined : { x: ringR }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      />

      <motion.div
        className="au-hero__disc"
        style={reduce ? undefined : { scale: discScale, y: discY, opacity: fade }}
        initial={{ opacity: 0, scale: 0.86 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.span
          className="au-hero__orbit"
          aria-hidden="true"
          animate={reduce ? undefined : { rotate: 360 }}
          transition={{ duration: 38, repeat: Infinity, ease: "linear" }}
        >
          <i />
          <i />
          <i />
        </motion.span>

        <h1 className="au-hero__title" aria-label="About">
          {LETTERS.map((c, i) => (
            <span className="au-hero__mask" key={i} aria-hidden="true">
              <motion.span
                style={{ display: "inline-block" }}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.07, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
              >
                {c}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          className="au-hero__tag"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
        >
          Caring for eyes across Maharashtra since 1995
        </motion.p>
      </motion.div>

      <motion.button
        type="button"
        className="au-hero__cue"
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <span>Scroll</span>
        <motion.i
          className="au-hero__cue-line"
          aria-hidden="true"
          animate={reduce ? undefined : { scaleY: [0.2, 1, 0.2], originY: 0 }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.button>
    </header>
  );
};

export default Hero;